// src/view/products/components/FavoriteButton.tsx
"use client";

import { Heart } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { get, post } from "@/src/api/apiHandler";
import { showToast } from "@/src/components/toast/showToast";
import useLoginStore from "@/src/store/useLoginStore";
import useUserStore from "@/src/store/useUser";

interface FavoriteButtonProps {
  prodId: number;
}

const FavoriteButton = ({ prodId }: FavoriteButtonProps) => {
  const [favorite, setFavorite] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { openLogin } = useLoginStore();
  const { dataUser } = useUserStore();

  /* ---------- استعلام اولیه ---------- */
  useEffect(() => {
    const fetchStatus = async () => {
      if (!dataUser?.token) return;
      try {
        const { data } = await get(
          `/favorite?id=${prodId}&type=product`
        ); // { active: boolean }
        setFavorite(Boolean(data?.active));
      } catch {
        /* بی‌صدا رد می‌شویم */
      }
    };

    fetchStatus();
  }, [prodId, dataUser?.token]);

  /* ---------- افزودن / حذف ---------- */
  const toggleFavorite = useCallback(async () => {
    if (!dataUser?.token) {
      openLogin();
      return;
    }
    if (loading) return;

    setLoading(true);
    try {
      const { data } = await post("/favorite", {
        ProductId: prodId,
        CommentId: null,
        ArticleId: null,
        favorite_type: "product",
        favorite_name: "heart",
      }); // { active: boolean }

      setFavorite(Boolean(data?.active));

      showToast(
        data.active
          ? "با موفقیت به لیست علاقه‌مندی اضافه شد"
          : "با موفقیت از لیست علاقه‌مندی حذف شد",
        data.active ? "success" : "warning"
      );
    } catch {
      showToast("مشکلی پیش آمد", "error");
    } finally {
      setLoading(false);
    }
  }, [prodId, dataUser?.token, loading, openLogin]);

  /* ---------- رندر ---------- */
  const ariaLabel = favorite
    ? "حذف از علاقه‌مندی‌ها"
    : "افزودن به علاقه‌مندی‌ها";

  return (
    <button
      type="button"
      onClick={toggleFavorite}
      disabled={loading}
      aria-label={ariaLabel}
      aria-pressed={favorite}
      title={
        dataUser?.token
          ? ariaLabel
          : "برای افزودن به علاقه‌مندی‌ها ابتدا وارد شوید"
      }
      className="transition-all rounded-full focus:outline-none hover:bg-pink-50 active:scale-95 disabled:opacity-50"
    >
      {/* آیکون تصویری، ولی متن مخفی برای SEO/ScreenReader */}
      <span className="sr-only">{ariaLabel}</span>
      <Heart
        aria-hidden="true"
        className="size-8 pt-2"
        fill={favorite ? "#d15267" : "#fff"}
        color={favorite ? "#d15267" : "#aaa"}
      />
    </button>
  );
};

export default FavoriteButton;
